import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { LayoutDashboard, Plus, UploadCloud, LogOut, Menu, Mailbox, ShieldCheck, CreditCard, Crown, Cloud } from "lucide-react";

const BASE_NAV = [
  { to: "/", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/new", label: "New Migration", icon: Plus },
  { to: "/bulk", label: "Bulk CSV Upload", icon: UploadCloud },
  { to: "/cloud", label: "Cloud Migration", icon: Cloud },
  { to: "/pricing", label: "Plans & Pricing", icon: CreditCard },
  { to: "/settings", label: "Settings", icon: ShieldCheck },
];

function Brand({ settings }) {
  const name = settings?.site_name || "MailShift";
  return (
    <div className="flex items-center gap-2">
      {settings?.logo_url ? (
        <img src={settings.logo_url} alt={name} className="h-9 w-9 rounded-md object-cover" />
      ) : (
        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary text-white">
          <Mailbox className="h-5 w-5" />
        </div>
      )}
      <div>
        <p className="font-heading text-lg font-bold leading-none tracking-tighter">{name}</p>
        <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Migration Engine</p>
      </div>
    </div>
  );
}

function NavItems({ onNavigate, isAdmin }) {
  const nav = [...BASE_NAV];
  if (isAdmin) nav.push({ to: "/admin", label: "Admin Panel", icon: Crown });
  return (
    <nav className="flex flex-col gap-1">
      {nav.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onNavigate}
          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-3 text-sm font-medium border-l-2 transition-colors duration-200 ${
              isActive ? "border-primary bg-accent text-primary"
                       : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted"
            }`
          }
        >
          <item.icon className="h-4 w-4" />
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

function SidebarInner({ onNavigate }) {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border px-5 py-5"><Brand settings={settings} /></div>
      <div className="flex-1 py-4"><NavItems onNavigate={onNavigate} isAdmin={user?.role === "admin"} /></div>
      <div className="border-t border-border p-4">
        <p className="mb-2 truncate font-mono text-xs text-muted-foreground" data-testid="sidebar-user-email">{user?.email}</p>
        <Button variant="outline" size="sm" className="w-full justify-start gap-2" data-testid="logout-button"
                onClick={() => { logout(); navigate("/"); }}>
          <LogOut className="h-4 w-4" /> Sign out
        </Button>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const { settings } = useSettings();
  return (
    <div className="flex min-h-screen bg-background">
      <aside className="hidden w-64 shrink-0 border-r border-border bg-card md:block">
        <div className="sticky top-0 h-screen"><SidebarInner /></div>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between border-b border-border bg-white/80 px-4 py-3 backdrop-blur-xl md:hidden">
          <span className="font-heading font-bold tracking-tight">{settings?.site_name || "MailShift"}</span>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0"><SidebarInner onNavigate={() => setOpen(false)} /></SheetContent>
          </Sheet>
        </header>
        <main className="min-w-0 flex-1 p-5 md:p-8">{children}</main>
      </div>
    </div>
  );
}
